// Autopilot: campaign list, three-step dialog, detail, items (paczka 12,
// artboards 05–09 and 11–14). Keep keys in sync with pl/appCampaigns.ts.
//
// Framing: scheduling posts on the artist's own accounts through the platform's
// official access. Nothing here promises what happens to a post after it goes out;
// no login-protocol name and no word about a programming interface.
//
// Technical addresses carrying old vocabulary STAY (addresses, not copy):
// `autopilot.batchSub`, the fourth key of the `planError` block.
import { appCampaignsK45 } from './appCampaigns.k45'
import { appCampaignsH45 } from './appCampaigns.h45'
import { appCampaignsP45 } from './appCampaigns.p45'

export const appCampaigns = {
  // The bar is the title alone (session 20, decision 2): the consent sentence goes on
  // Domi's word, and the remaining line repeated what the empty state already says.
  topbar: {
    title: 'Autopilot',
  },
  // The accounts strip above the content (session 20, decision 3): its label comes from
  // `appAccounts.title`, and this sentence stands in for the chips when nothing is connected.
  accounts: {
    none: 'No account is connected yet.',
    today: '{n} / {cap} today',
  },
  banner: {
    blockedSub: 'Publishing is temporarily unavailable - you can prepare and save a plan, it starts as soon as publishing is back.',
  },
  disabled: {
    title: 'Autopilot is not switched on for this account yet',
  },
  empty: {
    liveTitle: 'No campaigns yet',
    liveBody: 'Autopilot spreads the finished edits of your series across your accounts.',
    generator: 'Generate edits',
  },
  loading2: 'Loading campaigns…',
  title: 'Autopilot',
  error: 'Could not load your campaigns.',
  // An answer to an ACTION, not an item status (see the Polish file).
  apiError: {
    still_in_flight:
      'This post is still with the platform. Wait for it to finish - then you can try again.',
    gems_on_hold:
      'We cannot retry this - the post was already handed over and may be live on your account, so retrying would add a second one. Your tokens are on hold while we sort it out; we will be in touch.',
    already_published:
      'This post has already published - there is nothing to try again, and it can no longer be removed from the plan.',
  },
  status: {
    draft: 'draft',
    scheduled: 'scheduled',
    running: 'running',
    paused: 'paused',
    completed: 'finished',
    cancelled: 'cancelled',
    failed: 'failed',
  },
  itemStatus: {
    planned: 'planned',
    queued: 'queued',
    scheduled: 'scheduled',
    publishing: 'going out',
    published: 'published',
    failed: 'failed',
    cancelled: 'cancelled',
    skipped: 'skipped',
  },
  job: {
    published: 'published',
    sending: 'going out',
    scheduled: 'scheduled',
    failed: 'failed',
    cancelled: 'cancelled',
    awaiting: 'awaiting consent',
    held: 'on hold',
  },
  // `pluralForm('en', n)` returns only one|many; `few` exists for the parity gate.
  stats: {
    accounts: '{n} accounts',
    accounts_one: '{n} account',
    accounts_few: '{n} accounts',
    accounts_many: '{n} accounts',
    ready: '{n} edits ready',
    ready_one: '{n} edit ready',
    ready_few: '{n} edits ready',
    ready_many: '{n} edits ready',
    edits: '{n} edits',
    edits_one: '{n} edit',
    edits_few: '{n} edits',
    edits_many: '{n} edits',
  },
  list: {
    label: 'Campaign list',
    scheduled: 'Scheduled',
    // Cards, not rows (session 20, decision 4): there is no column header, so no
    // `thName/thMode/thStatus/thNext`. A campaign whose posts have no consent yet shows
    // `awaitingPill` instead of the "Draft" pill plus the list's ONLY button (`approve`);
    // `open` is the card link's label for a screen reader.
    awaitingPill: 'Awaiting consents',
    approve: 'Approve posts',
    open: 'Open campaign',
    next: 'next {when}',
    nextOn: 'next {when} · {handle}',
    frames: 'The campaign\'s first posts',
    morePosts: '{n} more posts',
    morePosts_one: '{n} more post',
    morePosts_few: '{n} more posts',
    morePosts_many: '{n} more posts',
    awaitingConsents: 'waiting for {n} consents',
    awaitingConsents_one: 'waiting for {n} consent',
    awaitingConsents_few: 'waiting for {n} consents',
    awaitingConsents_many: 'waiting for {n} consents',
    endedAt: '{status} {date}',
    failedRefunded: '{n} posts did not go out · tokens returned',
    failedRefunded_one: '{n} post did not go out · tokens returned',
    failedRefunded_few: '{n} posts did not go out · tokens returned',
    failedRefunded_many: '{n} posts did not go out · tokens returned',
  },
  mode: {
    manual: 'Manual',
    autopilot: 'Autopilot',
  },
  fields: {
    start: 'Start',
    end: 'End',
  },
  captions: {
    title: 'Post captions',
    pack: 'Caption pack',
    none: 'No pack - I will write captions when reviewing',
    hint: 'One pack for the whole publishing series. Review the completed captions in the plan.',
    nick: 'Artist handle',
    nickHint: '{@nick} is the profile to tag, e.g. @dominik. Enter it once for the whole publishing series.',
    nickRequired: 'Enter the artist handle for the selected caption pack.',
    nickInvalid: 'Use letters, digits, dots or underscores without spaces (up to 64 characters).',
    loadError: 'Could not load caption packs.',
    valuesError: 'Fill in or correct the variables for the selected caption pack.',
    change: 'Fix captions',
    caption: 'Post caption',
    planChanged: 'The caption pack changed after the preview. Refresh the plan and review the new captions before saving.',
    refreshPlan: 'Refresh plan',
    emptyPack: '0 captions',
    emptyPacks: 'A pack without captions cannot be chosen: {names}.',
    fillInLibrary: 'Add captions in the Library',
    // Rule 33: a field for a variable the CHOSEN pack uses. Labels only for the
    // variables today's parser knows (`lib/textTemplates.ts`).
    valueRequired: 'Enter a value.',
    vars: {
      artist: 'Artist',
      nick: 'Artist handle',
      timestamp: 'Moment',
      title: 'Track title',
    },
  },
  pick: {
    edits: 'Choose series',
    seriesHint: 'A series goes in whole - switch single edits off under “series edits”.',
    selectedSeries: 'series selected',
    all: 'all',
    none: 'none',
    noneAccounts: 'none',
    clear: 'clear',
    seriesEdits: 'series edits',
    summary: 'Selected {n} series',
    summary_one: 'Selected {n} series',
    summary_few: 'Selected {n} series',
    summary_many: 'Selected {n} series',
    accounts: 'Accounts',
    selectedAccounts: '{n} of {total} accounts',
    selectedAccounts_one: '{n} of {total} account',
    selectedAccounts_few: '{n} of {total} accounts',
    selectedAccounts_many: '{n} of {total} accounts',
    noAccounts: 'Connect an account first.',
    noSeries: 'You have no series with finished edits yet.',
    pickOne: 'Choose at least one series.',
    allSeries: 'All ({n})',
    searchSeries: 'Search series…',
  },
  series: {
    ready: '{n} edits · ready',
    ready_one: '{n} edit · ready',
    ready_few: '{n} edits · ready',
    ready_many: '{n} edits · ready',
    partial: '{n} of {total} ready · in progress',
    none: '0 ready - wait for the render',
    rendering: 'Rendering',
    readyOf: '{n} of {total} ready',
    editsTitle: 'Edits of “{name}”',
    selectedEdits: '{n} of {total} selected',
    notSelectable: 'not selectable',
    // Rule 30: three render-state words. An unfinished series is 'in the queue'.
    queued: 'in the queue',
    done: 'Done',
    untitled: 'Untitled series',
    editName: 'edit {n}',
  },
  window: {
    title: 'Publishing window',
    hint: 'Autopilot spreads {n} posts between the start and the end.',
    hint_one: 'Autopilot spreads {n} post between the start and the end.',
    hint_few: 'Autopilot spreads {n} posts between the start and the end.',
    hint_many: 'Autopilot spreads {n} posts between the start and the end.',
    startHint: 'in 2 h - the earliest possible',
    endHint: 'in 5 days - you can change it',
  },
  autopilot: {
    title: 'Autopilot',
    sub: 'Scheduling posts on your accounts through the platform’s official access. Nothing goes out without your consent to each post.',
    stepsLabel: 'Autopilot steps',
    steps: {
      series: 'Series',
      accounts: 'Accounts & window',
      plan: 'Plan & cost',
    },
    batchSub: 'Finished edits: {edits} · Connected accounts: {accounts}',
    previewTitle: 'How it lands',
    previewCount: 'Posts: {posts} · Days: {days}',
    previewFits: 'Posts: {fits} of {posts} · Days: {days}',
    advancedSub: 'Posts go out between 9:00 and 22:00, at most 5 a day per account, at least an hour apart · {tz}',
    nextAccounts: 'Next - accounts & window',
    nextPlan: 'Next - plan & cost',
    next: 'Review and confirm {n} posts',
    next_one: 'Review and confirm {n} post',
    next_few: 'Review and confirm {n} posts',
    next_many: 'Review and confirm {n} posts',
    draftName: 'Draft “{name}” - named after the series and the dates. Changing the window recalculates the plan.',
    costRow: '{n} posts × {each} tokens',
    costRow_one: '{n} post × {each} tokens',
    costRow_few: '{n} posts × {each} tokens',
    costRow_many: '{n} posts × {each} tokens',
    costTotal: '{n} tokens',
    costTotal_one: '{n} token',
    costTotal_few: '{n} tokens',
    costTotal_many: '{n} tokens',
    balanceRow: 'Balance {before} → {after}',
    balanceLabel: 'Balance',
    refundNote: 'For every post that does not go out, the tokens come back automatically.',
    notEnoughGems: 'You do not have enough tokens for this plan - it needs {need}, you have {have}.',
    fewerEdits: 'or choose fewer edits',
    extendTo: 'Extend to {date}',
    footNote: 'The next step is consent for each post - nothing goes out before that.',
    planning: 'Laying out the plan…',
  },
  // The publishing planner (session 22, artboards 01–03): four step rows like the
  // generator's, plus the right rail with the calendar preview. The steps themselves
  // (series, captions, window, accounts) reuse the keys above — the layout changed,
  // the vocabulary did not.
  split: {
    title: 'Accounts',
    cap: 'up to {n}',
    pickEdits: 'Choose edits',
    given: 'Assigned',
    even: 'evenly',
    custom: 'custom',
    of: '{n} / {total}',
    accountsN: '{n} accounts',
    accountsN_one: '{n} account',
    accountsN_few: '{n} accounts',
    accountsN_many: '{n} accounts',
    doneN: 'Done · {n} edits',
    doneN_one: 'Done · {n} edit',
    doneN_few: 'Done · {n} edits',
    doneN_many: 'Done · {n} edits',
    editsFor: 'Edits for {handle}',
    chosen: 'chosen',
    assignedTo: 'assigned to {handle}',
  },
  schedule: {
    title: 'Schedule',
    start: 'Start',
    end: 'End',
    doneN: 'Done · {n} posts',
    doneN_one: 'Done · {n} post',
    doneN_few: 'Done · {n} posts',
    doneN_many: 'Done · {n} posts',
  },
  rail: {
    posts: 'Posts',
    accounts: 'Accounts',
    days: 'Days',
    skipped: 'skipped pairs',
    show: 'Show',
    open: 'Show plan',
    schedule: 'Schedule',
    lack: 'Short by',
    topUp: 'Top up tokens',
  },
  pool: {
    alreadyPublished_one: '{n} already published',
    alreadyPublished_few: '{n} already published',
    alreadyPublished_many: '{n} already published',
    alreadyScheduled_one: '{n} already scheduled',
    alreadyScheduled_few: '{n} already scheduled',
    alreadyScheduled_many: '{n} already scheduled',
    skipUsed: 'Skip',
  },
  stage: {
    newPlan: 'New plan',
    showMonth: 'Show the whole month',
    showForm: 'Show the form',
    dayInPlan: 'in the plan: {n}',
    collapseStep: 'Collapse step {step}',
    scheduled: '{n} posts scheduled.',
    scheduled_one: '{n} post scheduled.',
    scheduled_few: '{n} posts scheduled.',
    scheduled_many: '{n} posts scheduled.',
    preview: 'Preview',
    previewN: 'Preview · {n} posts',
    previewN_one: 'Preview · {n} post',
    previewN_few: 'Preview · {n} posts',
    previewN_many: 'Preview · {n} posts',
    clear: 'Clear',
  },
  journey: {
    draft: 'Draft',
    savedAt: 'saved {when}',
    consent: 'Content approval',
    // „in batches of" wypada przez lint słownictwa (dawne słowo „batch" = seria); to samo
    // znaczenie bez niego, a PL zostaje przy „po {batch}".
    consentOf: '{n} / {total} · {batch} at a time',
    running: 'Running',
    afterConsent: 'after approval',
    waitsActivate: 'waiting for Start',
    pausedAt: 'paused {when}',
    approveNext: 'Approve the next {n}',
  },
  planner: {
    // Package 35a: step rows, the summary card, the 390 action bar.
    stepWindow: 'Start and end',
    postsDays: 'Posts {n} · Days {d}',
    postsFit: 'Posts {n} of {total}',
    cost: 'Cost',
    have: 'You have',
    left: 'Left',
    missing: 'Missing',
    dash: '-',
    loadMore: 'Load more',
    pill: {
      noEdits: 'no finished edits',
      noAccount: 'no account',
      noValue: 'variable has no value',
      paused: 'publishing paused',
      limitToday: 'limit for today',
      draft: 'draft',
      missingTokens: 'missing {n} tokens',
      missingTokens_one: 'missing {n} token',
      missingTokens_few: 'missing {n} tokens',
      missingTokens_many: 'missing {n} tokens',
      toPick: 'to pick',
      noPlan: 'no plan',
      readyEdits: '{n} ready',
    },
    done: {
      series: 'Done · {n} series',
      series_one: 'Done · {n} series',
      series_few: 'Done · {n} series',
      series_many: 'Done · {n} series',
      edits: 'Done · {n} of {total}',
      pack: 'Done · 1 pack',
      noPack: 'Done · no pack',
      days: 'Done · {n} days',
      days_one: 'Done · {n} day',
      days_few: 'Done · {n} days',
      days_many: 'Done · {n} days',
      accounts: 'Done · {n} accounts',
      accounts_one: 'Done · {n} account',
      accounts_few: 'Done · {n} accounts',
      accounts_many: 'Done · {n} accounts',
      platforms: 'Done · {n} platforms',
      platforms_one: 'Done · {n} platform',
      platforms_few: 'Done · {n} platforms',
      platforms_many: 'Done · {n} platforms',
    },
    fill: 'Fill in',
    noReadyEdits: 'no finished edits',
    generate: 'Generate edits',
    noAccount: 'no account',
    connect: 'Connect an account',
    poolDiff: '{n} / {total}',
    // The primary action with no number yet: nothing to count from (02a-02b).
    reviewAll: 'Review and confirm',
    // 'Back' from the series' edit grid to the list of series (dialog 03b).
    back: 'Back',
    steps: {
      edits: 'Edits',
      captions: 'Captions',
      accounts: 'Accounts',
      schedule: 'Schedule',
    },
    afterEdits: 'after you choose edits',
    noPack: 'No pack',
    series: '{n} series',
    series_one: '{n} series',
    series_few: '{n} series',
    series_many: '{n} series',
    to: 'to',
    preview: {
      title: 'Calendar preview',
      live: 'live',
      updating: 'Updating the preview…',
      empty: 'Choose edits and dates - the plan appears here.',
    },
    balance: 'Account balance {before} · {after} left',
    // Session 23 (artboard 04): the mark on a series card in row 1. 90 s is the LOWEST
    // of the three ceilings (a Reel), so one number covers the mark. It is information,
    // not an error - such an edit still goes out on a TikTok or a YouTube account.
    longEdits: '{n} edits > 90 s',
    longEdits_one: '{n} edit > 90 s',
    longEdits_few: '{n} edits > 90 s',
    longEdits_many: '{n} edits > 90 s',
    // The line under “Posts: n · Days: d”: (edit, account) pairs the account's platform
    // will not take. One sentence per ceiling, because each names that platform's format.
    skipped: '{n} edits skipped on {handle}: Reels up to 90 s',
    skipped_one: '{n} edit skipped on {handle}: Reels up to 90 s',
    skipped_few: '{n} edits skipped on {handle}: Reels up to 90 s',
    skipped_many: '{n} edits skipped on {handle}: Reels up to 90 s',
    skippedYt: '{n} edits skipped on {handle}: Shorts up to 3 min',
    skippedYt_one: '{n} edit skipped on {handle}: Shorts up to 3 min',
    skippedYt_few: '{n} edits skipped on {handle}: Shorts up to 3 min',
    skippedYt_many: '{n} edits skipped on {handle}: Shorts up to 3 min',
    skippedCap: '{n} edits skipped on {handle}: this account takes up to {max} s',
    skippedCap_one: '{n} edit skipped on {handle}: this account takes up to {max} s',
    skippedCap_few: '{n} edits skipped on {handle}: this account takes up to {max} s',
    skippedCap_many: '{n} edits skipped on {handle}: this account takes up to {max} s',
    skippedShort: '{n} edits skipped on {handle}: Reels from {min} s',
    skippedShort_one: '{n} edit skipped on {handle}: Reels from {min} s',
    skippedShort_few: '{n} edits skipped on {handle}: Reels from {min} s',
    skippedShort_many: '{n} edits skipped on {handle}: Reels from {min} s',
    // Row 4's summary: how many accounts and ON WHICH platforms (“3 of 4 accounts ·
    // TikTok, Instagram, YouTube”); the handles stand below, in the selection chips.
    accountsSummary: '{n} of {total} accounts · {platforms}',
    accountsSummary_one: '{n} of {total} account · {platforms}',
    accountsSummary_few: '{n} of {total} accounts · {platforms}',
    accountsSummary_many: '{n} of {total} accounts · {platforms}',
  },
  planError: {
    window_too_small: 'The window is too short for all posts - {n} fit.',
    no_connected_accounts: 'Connect an account first.',
    empty_batch: 'This series has no finished edits.',
    noReadyEdits: 'This series has no finished edits.',
    unknown: 'We could not plan this campaign.',
    // The platform ceilings (Reels 90 s, Shorts 3 min) refused EVERY edit: a retry changes nothing.
    no_edits_fit_platforms: 'None of the selected edits fits the length limit of the chosen accounts (Reels up to 90 s, Shorts up to 3 min).',
    changeAccounts: 'Change accounts',
    startSoonest: 'Set the start 2 h from now',
  },
  day: {
    posts: '{n} posts',
    posts_one: '{n} post',
    posts_few: '{n} posts',
    posts_many: '{n} posts',
  },
  detail: {
    awaiting: '{n} posts still need your confirmation',
    awaiting_one: '{n} post still needs your confirmation',
    awaiting_few: '{n} posts still need your confirmation',
    awaiting_many: '{n} posts still need your confirmation',
    pause: 'Pause',
    resume: 'Resume',
    cancel: 'Cancel campaign',
    cancelConfirm: 'Cancel the campaign “{name}”?',
    cancelBody: 'Posts that have not gone out will be stopped. Published ones stay on your accounts.',
    cancelWillStop: 'Posts stopped',
    cancelWillRefund: 'Tokens back in your pool',
    resumeTitle: 'Resume the campaign?',
    resumeMissed: 'Posts skipped: {n} - their time passed while the campaign was paused, tokens returned.',
    resumeRest: 'The remaining {n} posts go out as planned.',
    resumeRest_one: 'The remaining {n} post goes out as planned.',
    resumeRest_few: 'The remaining {n} posts go out as planned.',
    resumeRest_many: 'The remaining {n} posts go out as planned.',
    resumeRefunded: 'Tokens returned',
    keep: 'Keep it',
    keepPaused: 'Keep it paused',
    posts: 'Posts',
    filterAll: 'All',
    filterAttention: 'Needs fixing',
    published: 'Published',
    reviewCta: 'Review and confirm',
    progress: 'Progress',
    progressOf: '{n} of {total} published',
    limits: 'Account limits',
    limitToday: '{n} / {max} today',
    limitsNote: 'The daily limit protects the account - the next posts go out tomorrow.',
    consents: 'Consents',
    consentsCollected: 'Collected',
    consentsOf: '{n} of {total}',
    consentsDate: 'Date',
    consentsVersion: 'Consent version',
    consentsNote: 'Without a stored consent a post is not sent - we check this right before sending.',
    nothingToFix: 'Nothing needs fixing.',
    // The row action for a post waiting for consent - not the batch one.
    confirmPost: 'Confirm post',
    // Dialogs 06a-06c: one sentence with the number, no second sentence about refunds.
    cancelStops: '{n} scheduled posts will be cancelled.',
    cancelStops_one: '{n} scheduled post will be cancelled.',
    cancelStops_few: '{n} scheduled posts will be cancelled.',
    cancelStops_many: '{n} scheduled posts will be cancelled.',
    skippedPosts: 'Skipped posts',
    postWillNotGo: 'The post will not go out.',
  },
  actions: {
    // The label of the panel's ONE primary button (session 20, decision 1): the action,
    // not the name of the screen. `autopilot.title` stays the dialog's title.
    create: 'Create a campaign',
    activate: 'Start',
    cancel: 'Cancel',
  },
  toast: {
    paused: 'Paused · {n} posts waiting',
    paused_one: 'Paused · {n} post waiting',
    paused_few: 'Paused · {n} posts waiting',
    paused_many: 'Paused · {n} posts waiting',
    resumed: 'Resumed',
    retried: 'Retried the post · -{n} tokens',
    retried_one: 'Retried the post · -{n} token',
    retried_few: 'Retried the post · -{n} tokens',
    retried_many: 'Retried the post · -{n} tokens',
    activated: 'Campaign started',
    removedFromPlan: 'Removed from the plan · +{n} tokens',
    removedFromPlan_one: 'Removed from the plan · +{n} token',
    removedFromPlan_few: 'Removed from the plan · +{n} tokens',
    removedFromPlan_many: 'Removed from the plan · +{n} tokens',
    cancelItemTitle: 'Remove from the plan?',
    cancelItemBody: 'This post will not go out. If it was already paid for, the tokens come back to your pool.',
    cancelItemRefund: 'Tokens back',
    retryItemTitle: 'Retry this post?',
    retryItemBody: 'The post goes back into the plan at the next free slot.',
    retryItemCost: 'Cost of retrying',
  },
  item: {
    retry: 'Try again',
    retryLabel: 'Try again ({n} tokens)',
    retryLabel_one: 'Try again ({n} token)',
    retryLabel_few: 'Try again ({n} tokens)',
    retryLabel_many: 'Try again ({n} tokens)',
    cancel: 'Remove from plan',
    openPost: 'Open post',
    scheduledFor: 'Goes out {when}',
    publishedAt: 'Went out {time}',
    publishingNow: 'Going out now',
    tomorrowAt: 'tomorrow {time}',
    noThumb: 'no frame',
    retryCost: 'Trying again costs 2 tokens',
    fixText: 'Fix the text',
    fixPrivacy: 'Fix the visibility',
    gemsHeld: 'tokens on hold',
    nothingCharged: 'nothing was charged',
    refunded: 'tokens returned',
  },
  reason: {
    consent_missing: 'Waiting for your confirmation',
    daily_cap: 'Moved - this account already has 5 posts today. Goes out {when}.',
    min_gap: 'Moved - posts on one account are at least an hour apart. Goes out {when}.',
    past_due: 'On hold - your invoice needs paying',
    connection_expired: 'On hold - reconnect this account',
    connection_suspended: 'On hold - your plan no longer covers this account',
    privacy_unavailable: 'On hold - the visibility you chose is no longer offered for this account',
    platform_cap: 'Moved to tomorrow - the {platformGen} daily limit for this account. Goes out {when}.',
    rate_limited: 'Moved - {platform} asked us to slow down. Goes out {when}.',
    auth_removed: 'On hold - access to this account was withdrawn',
    provider_timeout: 'Failed - no answer within 24 h. Tokens returned.',
    content_gate: 'Blocked - the text needs changing before this can go out',
    provider_quota: 'Moved - we are at our publishing capacity right now. Goes out {when}.',
    render_too_large: 'File too large - our publishing partner caps the size. Nothing was taken from your tokens; a shorter edit should go through.',
    withdrawal_failed: 'Could not be withdrawn - the post was already handed over. Your tokens are on hold while we sort it out; we will be in touch.',
    feature_disabled: 'On hold - publishing is switched off for now. The post is waiting, nothing was charged.',
    deferral_expired: 'Failed - it could not be fitted in within a day. Tokens returned.',
    unknown: 'On hold - we are checking what happened.',
  },
  refunded: 'Tokens returned',
  // A failed read: one sentence, one way out, one code (language rule 8).
  loadError: {
    planner: 'We could not load the Planner.',
    plan: 'We could not load the plan.',
  },
  // A paid action with too low a balance: no sentences, just the number table.
  noTokens: {
    title: 'Not enough tokens',
    topup: 'Top up tokens',
  },
  // 45-B (C0): poddrzewa k45 / h45 / p45 z plikow paczek PK / PH / PP2.
  ...appCampaignsK45,
  ...appCampaignsH45,
  ...appCampaignsP45,
}
